import React from 'react';
import { addHours } from 'date-fns';
import { ParticipantRow } from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorPeopleListView';
import GetCoachingSessionsAndRecordingsContainer from 'coaching/containers/GetCoachingSessionsAndRecordingsContainer';
import CoachingCoordinatorParticipantSidebar from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorParticipantSidebar';
import { CoachingUserType } from 'globalTypes';
import { GetUnfulfilledParticipantCoachPairs_coaching_unfulfilledParticipantCoachPairs_participant } from 'coaching/queries/__generated__/GetUnfulfilledParticipantCoachPairs';
import {
  CoachingInstanceGQL,
  CoachingSessionGQL,
} from 'coaching/containers/GetCoachingSessionsAndRecordingsContainer';

export type Participant = GetUnfulfilledParticipantCoachPairs_coaching_unfulfilledParticipantCoachPairs_participant;

/**
 * Converts from the new schema-stitched Participant type to the ParticipantRow type
 * expected by CoachingCoordinatorParticipantSidebar.
 *
 * The old type is still used in the queries GetCoachingSessionInstancesForTimeframe
 * (no-show page) and GetParticipantList (people list, via CoachingCoordinatorContainer).
 * We can use this as we transition those views to use schema-stitched queries with the new
 * Participant type, after which we can deprecate this function along with ParticipantRow.
 */
export function participantToParticipantRow(
  participant: Participant | ParticipantRow
): ParticipantRow {
  return {
    ...participant,
    __typename: 'CoachingParticipant',
    // purchaserUuid is non-nullable on participant & should be required in the coaching gql type,
    // but it's not right now. Kludge here until we fix it in coaching
    purchaserUuid: participant.purchaserUuid!,
    // the following three should be nullable in gql. ParticipantRow is wrong
    studentUuid: participant.studentUuid || '',
    studentGradeLevel: participant.studentGradeLevel || '',
    parentName: participant.parentName || '',
    // TODO: these two should be added to the SessionTools gql type in coaching & pulled from participant.sessionTools.
    // distanceLearningPermalink is necessary for the DL button and "recordings" section in the sidebar.
    distanceLearningPermalink: '', // should actually be nonnull
    distanceLearningUuid: '', // should actually be nullable
    // do we still need this?
    whiteboardUrl: '',
    enrolledInOrganizationTutoring: false,
    organizationTutoring: null
  };
}

// For future sessions, filters out past instances and sorts in ascending chronological order.
// For past sessions, filters out future instances and sorts in descending chronological order.
function sortAndFilterSessions(
  sessions: CoachingSessionGQL[],
  isPast: boolean
): CoachingSessionGQL[] {
  const isValidDate = (startDate: Date | string) => {
    const date = new Date(startDate);
    // For upcoming instances, we need to compare the instance's _end_ time to the current date
    // in order to include ongoing instances, so we add an hour to the instance's start time.
    return isPast ? date < new Date() : addHours(date, 1) > new Date();
  };

  const sortByDate = (a: CoachingInstanceGQL, b: CoachingInstanceGQL) =>
    (a.startDateTime < b.startDateTime ? -1 : a.startDateTime > b.startDateTime ? 1 : 0) *
    (isPast ? -1 : 1); // Flips the sort order (from ascending to descending) if isPast is true

  return (
    sessions
      .map(session => ({
        ...session,
        instances: session.instances.filter(i => isValidDate(i.startDateTime)).sort(sortByDate),
      }))
      // Filter out sessions where all instances have been filtered out.
      // This shouldn't actually happen unless there is a bug, but it's necessary as a safeguard
      // because the SessionMetadata component will throw if we have a session with no instances.
      .filter(session => session.instances.length > 0)
      .sort((a, b) => sortByDate(a.instances[0], b.instances[0]))
  );
}

type Props = {
  participant: ParticipantRow | Participant;
  onShowScheduleSessionView?: () => void;
};

export default function CoachingCoordinatorParticipantSidebarView({
  participant,
  onShowScheduleSessionView,
}: Props) {
  const participantRow =
    participant.__typename === 'CoachingParticipant'
      ? participant
      : participantToParticipantRow(participant);
  return (
    <GetCoachingSessionsAndRecordingsContainer
      participantUuid={participant.uuid}
      userType={CoachingUserType.participant}
      distanceLearningSessionUuid={participantRow.distanceLearningUuid}>
      {({ upcomingSessions, previousSessions, sessionMedia, loading, error }) => {
        return (
          <CoachingCoordinatorParticipantSidebar
            participant={participantRow}
            onShowScheduleSessionView={onShowScheduleSessionView}
            upcomingSessions={sortAndFilterSessions(upcomingSessions ?? [], false)}
            previousSessions={sortAndFilterSessions(previousSessions ?? [], true)}
            sessionMedia={sessionMedia}
            sessionsLoading={loading}
            sessionsError={error}
          />
        );
      }}
    </GetCoachingSessionsAndRecordingsContainer>
  );
}
